import type { codec } from "@irriga-mais/proto";
import { postDataToCodec, requestCodecReport } from "codec-http-integration";
import { Eye, EyeOff, HelpCircle, RefreshCw } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Modal, useToast } from "@/components";
import Button from "@/components/ui/Button";
import { ModalFooter } from "@/components/ui/ModalFooter";
import { appConfig } from "@/utils/config";

interface CodecConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function CodecConfigModal({ isOpen, onClose }: CodecConfigModalProps) {
  const { showError, showSuccess, showWarning, showInfo } = useToast();

  const [ip, setIp] = useState(appConfig.localWifi.ip);
  const [username, setUsername] = useState(appConfig.localWifi.username);
  const [password, setPassword] = useState(appConfig.localWifi.password);
  const [showHttpPassword, setShowHttpPassword] = useState(false);

  const [identifier, setIdentifier] = useState<string>("");
  const [identifierLoading, setIdentifierLoading] = useState(false);
  const [identifierError, setIdentifierError] = useState<string | null>(null);

  const [wifiSSID, setWifiSSID] = useState("");
  const [wifiPassword, setWifiPassword] = useState("");
  const [showWifiPassword, setShowWifiPassword] = useState(false);

  const [sending, setSending] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const canSend = useMemo(() => {
    return !!wifiSSID && !!wifiPassword && !!ip && !!username && !!password;
  }, [wifiSSID, wifiPassword, ip, username, password]);

  const fetchIdentifier = useCallback(async () => {
    setIdentifierLoading(true);
    setIdentifierError(null);
    try {
      const data = await requestCodecReport(ip, "info", {
        user: username,
        pass: password,
      });
      console.log("Received codec report:", data);
      // Extract codec identifier
      const licIdentifier = (data as any)?.info?.codec_id ?? (data as any)?.info?.id;
      if (licIdentifier) {
        setIdentifier(String(licIdentifier));
      } else {
        setIdentifier("");
        setIdentifierError("Identificador não encontrado no relatório retornado.");
        showWarning({
          message: "Não foi possível obter o identificador do CODEK.",
        });
      }
    } catch (err: any) {
      const detail = err?.message || String(err);
      setIdentifier("");
      setIdentifierError(detail);
      // Toast error, as requested
      showError({
        message: `Falha ao conectar ao CODEK: ${detail}`,
        duration: 6000,
        position: "center",
        attention: true,
      });
    } finally {
      setIdentifierLoading(false);
    }
  }, [ip, username, password, showError, showWarning]);

  // useEffect(() => {
  //   if (isOpen) {
  //     // Attempt to prefetch identifier on open using default credentials
  //     fetchIdentifier();
  //   } else {
  //     // reset ephemeral state when closing
  //     setIdentifierError(null);
  //   }
  // }, [isOpen]);

  const handleSend = async () => {
    const wifi: codec.in_.config.IWifiConfig = {
      ssid: wifiSSID,
      password: wifiPassword,
    };
    const packet: codec.in_.IIncomingPacket = {
      id: Math.round(Date.now() / 1000),
      config: {
        wifi,
      },
    };

    try {
      setSending(true);
      await postDataToCodec(ip, packet, { user: username, pass: password });
      showSuccess({
        message: "Configuração enviada para o CODEK com sucesso.",
      });
      onClose();
    } catch (err: any) {
      const detail = err?.message || String(err);
      showError({
        message: `Falha ao enviar configuração: ${detail}`,
        duration: 6000,
        position: "center",
        attention: true,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Configurar CODEK" size="xl">
      <div className="CodecConfigModal space-y-6">
        {/* Help banner/button */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">Conecte-se ao Wi‑Fi local do CODEK para configurar.</p>
          <Button
            variant="secondary"
            size="sm"
            icon={<HelpCircle className="h-4 w-4" />}
            onClick={() => setShowHelp(true)}
          >
            Ajuda
          </Button>
        </div>

        {/* HTTP auth + target */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">IP do CODEK</label>
            <input
              type="text"
              value={ip}
              onChange={(e) => setIp(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
              placeholder="192.168.4.1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Usuário (HTTP)</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
              placeholder="byagro"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Senha (HTTP)</label>
            <div className="relative">
              <input
                type={showHttpPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none pr-12"
                placeholder="Senha HTTP"
              />
              <Button
                type="button"
                variant="ghost"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                onClick={() => setShowHttpPassword((v) => !v)}
                aria-label={showHttpPassword ? "Ocultar senha" : "Mostrar senha"}
              >
                {showHttpPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Identifier field with refresh */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Identificador do CODEK</label>
          <div className="relative">
            <input
              type="text"
              value={identifier}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 pr-12"
              placeholder={identifierLoading ? "Carregando..." : "—"}
            />
            <Button
              type="button"
              aria-label="Atualizar identificador"
              onClick={fetchIdentifier}
              loading={identifierLoading}
              variant="ghost"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              <RefreshCw className="h-5 w-5" />
            </Button>
          </div>
          <p className="mt-1 text-xs text-gray-500">Obtido via relatório local (status) do CODEK.</p>
        </div>

        {/* Error card when identifier request fails */}
        {identifierError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-800 font-medium mb-1">Não foi possível conectar ao CODEK</p>
            <p className="text-sm text-red-700 whitespace-pre-wrap break-words">{identifierError}</p>
          </div>
        )}

        {/* Wi‑Fi credentials to set on the CODEK */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">SSID do Wi‑Fi</label>
            <input
              type="text"
              value={wifiSSID}
              onChange={(e) => setWifiSSID(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
              placeholder="Nome da rede Wi‑Fi"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Senha do Wi‑Fi</label>
            <div className="relative">
              <input
                type={showWifiPassword ? "text" : "password"}
                value={wifiPassword}
                onChange={(e) => setWifiPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none pr-12"
                placeholder="Senha da rede Wi‑Fi"
              />
              <Button
                type="button"
                aria-label={showWifiPassword ? "Ocultar senha" : "Mostrar senha"}
                onClick={() => setShowWifiPassword((v) => !v)}
                variant="ghost"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showWifiPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>

        <ModalFooter>
          <Button variant="primary" className="flex-1" onClick={handleSend} disabled={!canSend} loading={sending}>
            Enviar
          </Button>
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            Cancelar
          </Button>
        </ModalFooter>
      </div>

      {/* Help Modal */}
      <Modal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Como conectar ao CODEK" size="md">
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            1. No seu dispositivo, conecte-se à rede Wi‑Fi do CODEK com SSID
            <span className="font-mono"> CODEC_&lt;identificador&gt; </span> e senha
            <span className="font-mono"> 0123456789</span>.
          </p>
          <p className="text-sm text-gray-700">
            2. Com o Wi‑Fi local conectado, este modal usará o IP
            <span className="font-mono"> {appConfig.localWifi.ip} </span> e as credenciais HTTP para ler o identificador
            e enviar a configuração.
          </p>
          <p className="text-sm text-gray-700">
            3. Preencha o SSID e a senha da sua rede com acesso à internet e clique em <b>Enviar</b>.
          </p>
        </div>
        <ModalFooter>
          <Button variant="primary" className="flex-1" onClick={() => setShowHelp(false)}>
            Entendi
          </Button>
        </ModalFooter>
      </Modal>
    </Modal>
  );
}

export default CodecConfigModal;
